// "Relaterat" — länkmodul som visar 3 relaterade format-longtails
// + 2 jämförelsesidor + valfria syskon-hubbar (samma stad, andra tjänster).
// Stärker internal linking från city×format-hubbar mot pelare och AEO-sidor.

import { Link } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";

export interface RelatedHubLink {
  label: string;
  path: string;
  description?: string;
}

export interface RelatedHubsProps {
  /** Eyebrow text — ofta "Relaterat" eller "Utforska vidare" */
  eyebrow?: string;
  /** Sektionsrubrik */
  title?: React.ReactNode;
  /** 2–4 relevanta format-longtails (t.ex. dronarfilm, 3d-animation) */
  formats: RelatedHubLink[];
  /** 1–3 jämförelsesidor */
  comparisons: RelatedHubLink[];
  /** Valfritt — syskon-hubbar i samma stad (filmbyra, videobyra etc) */
  siblings?: RelatedHubLink[];
}

function LinkCard({ link }: { link: RelatedHubLink }) {
  return (
    <Link
      to={link.path}
      className="group flex flex-col rounded-2xl border border-white/10 bg-surface/40 p-5 transition hover:border-mint/40 hover:bg-surface/60"
    >
      <span className="text-base font-medium text-foreground group-hover:text-mint">
        {link.label}
      </span>
      {link.description && (
        <span className="mt-1 text-sm text-foreground/70">{link.description}</span>
      )}
      <span className="mt-3 text-xs text-mint opacity-0 transition group-hover:opacity-100">
        Läs mer →
      </span>
    </Link>
  );
}

function ColumnGroup({
  label,
  items,
}: {
  label: string;
  items: RelatedHubLink[];
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-3 text-xs uppercase tracking-widest text-mint">{label}</div>
      <div className="grid gap-3">
        {items.map((l) => (
          <LinkCard key={l.path} link={l} />
        ))}
      </div>
    </div>
  );
}

export function RelatedHubs({
  eyebrow = "Relaterat",
  title = <>Utforska vidare</>,
  formats,
  comparisons,
  siblings = [],
}: RelatedHubsProps) {
  const columns = (siblings.length > 0 ? 3 : 2);
  const gridCls =
    columns === 3
      ? "grid gap-8 md:grid-cols-3"
      : "grid gap-8 md:grid-cols-2";

  return (
    <Section className="py-10">
      <EyebrowHeading eyebrow={eyebrow} title={title} />
      <div className={`mt-10 ${gridCls}`}>
        <ColumnGroup label="Format vi gör" items={formats} />
        {siblings.length > 0 && <ColumnGroup label="Samma stad" items={siblings} />}
        <ColumnGroup label="Jämför alternativ" items={comparisons} />
      </div>
    </Section>
  );
}
